import { inject, Injectable } from '@angular/core';
import { COMMON_VALIDATION, EMAIL, INDIVIDUAL_NAME, Method, NRIC } from '@core/data-access';
import { get18YearsAgo } from '@core/util';
import { PortalFieldConfig, uppercaseParser } from '@portal/form/data-access';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, EMPTY, filter, map, startWith, tap } from 'rxjs';
import { PERSONNEL_OTHER } from '../const/app.const';
import { BusinessDeclarationUsPersonKeys, UsPersonModelKeys } from '../const/form-field-key.const';
import { AuthorizedPersonnelOption, UsPersonOption } from '../type';
import { constraintAppointmentOptions } from '../util';
import { MobileFormConfigService } from './mobile-form-config.service';
import { ApplicationStore, FinancialRequiredStore } from '../store';
import { FormGroup } from '@angular/forms';
import { UsPersonModel } from '../model';

@Injectable({ providedIn: 'root' })
export class UsPersonFormConfigService {
  private readonly mobileFormConfigService = inject(MobileFormConfigService);
  private readonly applicationStore = inject(ApplicationStore);
  private readonly portalPersonnelOptionsMap$ = inject(FinancialRequiredStore).portalPersonnelOptionsMap$;
  private readonly portalUsPersonOptionsMap$ = new BehaviorSubject<Map<string, UsPersonOption>>(new Map());
  private readonly portalUsPersonOptions$ = new BehaviorSubject<UsPersonOption[]>([]);
  private readonly corppassPersonnelOptionsMap$ = new BehaviorSubject<Map<string, AuthorizedPersonnelOption>>(new Map());

  initShareholderFormFields(): PortalFieldConfig[] {
    return [
      {
        template: `<div class='portal-paragraph'>Does the Legal Entity have any US directors, shareholders or Ultimate Beneficiary Owners (UBOs) with more than 25% ownership or equivalent who are US Persons? <span class="font-bold">If yes, please complete the following.</span></div>`,
      },
      {
        template: `<p>Shareholder is not available!</p>`,
        expressions: { hide: 'model.listOfShareholders' },
      },
      {
        expressions: { hide: '!model.listOfShareholders' },
        fieldGroup: [
          {
            key: BusinessDeclarationUsPersonKeys.listOfShareholders,
            type: 'form-array-us-person',
            fieldArray: {
              fieldGroupClassName: 'field-columns',
              fieldGroup: this.initUsPersonFormFields(),
            },
            validators: {
              validation: [
                'uniqueNricInArray',
                {
                  name: 'only-one-keyman',
                  options: { keyName: UsPersonModelKeys.appointment },
                },
              ],
            },
            hooks: {
              onInit: ({ formControl }) => {
                const formControlChanges$ = formControl?.valueChanges.pipe(startWith(formControl?.value), distinctUntilChanged()) || EMPTY;

                /**
                 * Disable Selected Options
                 * @param uuids
                 */
                const disableSelectedOptions = (uuids: string[]) => {
                  const usPersonOptions = Array.from(this.portalUsPersonOptionsMap$.getValue().values()).map(v => {
                    v.disabled = v.value !== PERSONNEL_OTHER && uuids.includes(v.value);
                    return v;
                  });
                  this.portalUsPersonOptions$.next(usPersonOptions);
                };

                return combineLatest([
                  formControlChanges$.pipe(
                    map(() =>
                      Array.isArray(formControl?.getRawValue())
                        ? formControl
                            ?.getRawValue()
                            ?.map((v: UsPersonModel) => v?.[UsPersonModelKeys.uuid])
                            .filter((v: UsPersonModel) => !!v)
                        : [],
                    ),
                  ),
                  this.portalPersonnelOptionsMap$,
                  this.portalUsPersonOptionsMap$,
                ]).pipe(
                  debounceTime(300),
                  tap(([uuids]: [any[], Map<string, AuthorizedPersonnelOption>, Map<string, UsPersonOption>]) => {
                    disableSelectedOptions(uuids);
                  }),
                );
              },
            },
          },
        ],
      },
      {
        template: `
              <div class="portal-paragraph"><span class="font-bold">Note:</span> These individuals are required to submit <a class="underline text-primary" href="https://www.cimb.com.sg/content/dam/cimbsg/personal/document/forms/regulations-policies/foreign-account-tax-compliance-act/fatca-w9.pdf" target="_blank">W9 form</a> to our RM.</div>
            `,
      },
    ];
  }

  private initUsPersonFormFields(): PortalFieldConfig[] {
    return [
      {
        key: UsPersonModelKeys.metadata,
        className: 'hidden',
        fieldGroup: [
          {
            key: 'uuid',
            // type: 'input',
            // props: { label: 'metadata.uuid' },
          },
        ],
      },
      {
        key: UsPersonModelKeys.method,
        // type: 'input',
        // props: { label: 'Method', disabled: true },
      },
      {
        key: UsPersonModelKeys.self,
        // type: 'input',
        // props: { label: 'Self', displayWith: value => JSON.stringify(value), disabled: true },
      },
      {
        key: UsPersonModelKeys.uuid,
        type: 'mat-select',
        expressions: { 'props.disabled': field => this.isNotOtherOption(field) },
        resetOnHide: false,
        hooks: {
          onInit: ({ form, formControl, parent, model }) => {
            const formControlChanges$ = formControl?.valueChanges.pipe(startWith(formControl?.value), distinctUntilChanged()) || EMPTY;
            const appPersonnelOptionsMap$ = this.applicationStore.appPersonnelOptionsMap$;
            const appUsPersonOptionsMap$ = this.applicationStore.appUsPersonOptionsMap$;

            const getUuidSelected = (): string | null => {
              const uuid = formControl?.getRawValue();
              if (uuid === PERSONNEL_OTHER || typeof uuid !== 'string' || !model?.[UsPersonModelKeys.uuid]) return null;
              return uuid;
            };

            const getPortalOptions = () => {
              const portalPersonnelOptionsMap = this.portalPersonnelOptionsMap$.getValue();
              const selectedOptions = Array.from(portalPersonnelOptionsMap.values()).filter(v =>
                v.personnel?.appointment?.includes('SHAREHOLDER'),
              );
              const selectedMap = new Map<string, AuthorizedPersonnelOption>(selectedOptions.map(v => [v.value, v]));
              const corppassOptionsMap = new Map(
                Array.from(appPersonnelOptionsMap$.getValue().entries()).filter(([key, value]) => {
                  return !portalPersonnelOptionsMap.has(key) && value.personnel?.method === Method.corppass;
                }),
              );

              return {
                selected: {
                  map: selectedMap,
                  options: selectedOptions,
                },
                corppass: {
                  map: corppassOptionsMap,
                  options: Array.from(corppassOptionsMap.values()).filter(v => v.value !== PERSONNEL_OTHER),
                },
              };
            };

            const transformToUsPersonOptionMap = (
              personnelOptions: AuthorizedPersonnelOption[],
              updateFn?: (us: UsPersonModel) => void,
            ) => {
              const options = personnelOptions.map<UsPersonOption>(v => {
                const { personnel, ...us } = v;
                const usPerson = UsPersonModel.fromJson(personnel);
                const uuid = personnel?.uuid === 'PERSONNEL_OTHER' ? personnel?.metadata?.uuid : personnel?.uuid;

                usPerson.uuid = uuid || null;

                if (typeof updateFn === 'function') {
                  updateFn(usPerson);
                }

                return { ...us, usPerson };
              });

              return {
                map: new Map<string, UsPersonOption>(options.map(o => [o.value, o])), // unique by value
                options,
              };
            };

            /**
             * Populate Form with Selected Options
             * @param option
             */
            const populateForm = (option?: UsPersonOption): void => {
              if (option?.usPerson && form instanceof FormGroup) {
                form.patchValue(option.usPerson);
              }
            };

            /**
             * Remove Form Control if Selected Option is Not Available
             */
            const removeFormControl = () => {
              const uuid = getUuidSelected();
              if (!uuid) return;

              const option = this.portalUsPersonOptionsMap$.getValue().get(uuid);
              const parentProps = parent?.parent?.props;
              const usPersons: UsPersonModel[] = parent?.parent?.model ?? [];

              if (!option && model?.[UsPersonModelKeys.uuid] !== PERSONNEL_OTHER) {
                const indexToRemove = usPersons.findIndex(v => v[UsPersonModelKeys.uuid] === formControl?.value);
                if (indexToRemove !== -1) {
                  parentProps?.['remove']?.(indexToRemove);
                }
              }
            };

            const populateFormBasedOnUsPersonOptionMap = (usPersonOptionMap: Map<string, UsPersonOption>) => {
              const uuid = getUuidSelected();
              if (!uuid) return;

              const option = usPersonOptionMap.get(uuid);
              populateForm(option);
            };

            /**
             * Populate Method to CorpPass if Selected Option is in Remaining List
             * @param remainingPersonnelOptionMap
             */
            const populateMethodBasedOnRemainingPersonnelOptionMap = (
              remainingPersonnelOptionMap: Map<string, AuthorizedPersonnelOption>,
            ) => {
              const uuid = getUuidSelected();
              if (!uuid) return;

              const option = remainingPersonnelOptionMap.get(uuid);
              if (option && form instanceof FormGroup) {
                form.patchValue({ [UsPersonModelKeys.method]: Method.corppass });
              }
            };

            const mergePortalPersonnelOptionsMapToPortalUsPersonOptionsMap = () => {
              const portalOptions = getPortalOptions();
              const portalPersonnelOptionsSelected = transformToUsPersonOptionMap(portalOptions.selected.options, us => {
                us.method = Method.populate;
              }).options;
              const corppassPersonnelOptions = transformToUsPersonOptionMap(portalOptions.corppass.options, us => {
                us.method = Method.corppass;
              }).options;

              const options = [
                ...portalPersonnelOptionsSelected,
                ...corppassPersonnelOptions,
                ...appUsPersonOptionsMap$.getValue().values(),
              ];

              const portalUsPersonOptionsMap = new Map<string, UsPersonOption>(options.map(o => [o.value, o])); // unique by value

              this.portalUsPersonOptionsMap$.next(portalUsPersonOptionsMap);
              this.corppassPersonnelOptionsMap$.next(portalOptions.corppass.map);

              // console.groupCollapsed('Portal Us Person Options');
              // console.log('appUsPersonOptionsMap$');
              // console.table(Array.from(this.applicationStore.appUsPersonOptionsMap$.getValue().values()));
              // console.log('corppassPersonnelOptionsMap$');
              // console.table(Array.from(this.corppassPersonnelOptionsMap$.getValue().values()));
              // console.log('portalPersonnelOptionsMap$');
              // console.table(Array.from(this.portalPersonnelOptionsMap$.getValue().values()));
              // console.log('portalUsPersonOptionsMap$');
              // console.table(Array.from(this.portalUsPersonOptionsMap$.getValue().values()));
              // console.groupEnd();
            };

            return combineLatest([
              formControlChanges$.pipe(
                filter(() => !!getUuidSelected()),
                distinctUntilChanged(),
                tap(() => {
                  // const portalUsPersonOptionsMap = this.portalUsPersonOptionsMap$.getValue();
                  // populateFormBasedOnUsPersonOptionMap(portalUsPersonOptionsMap);

                  const uuid = getUuidSelected();
                  if (!uuid || !(form instanceof FormGroup)) return;

                  const portalOptions = getPortalOptions();

                  const portalPersonnelSelectedMap = transformToUsPersonOptionMap(portalOptions.selected.options, us => {
                    us.method = Method.populate;
                  }).map;

                  const corppassPersonnelMap = transformToUsPersonOptionMap(portalOptions.corppass.options, us => {
                    us.method = Method.corppass;
                  }).map;

                  const existingPortalPersonnelSelected = portalPersonnelSelectedMap.get(uuid);
                  const existingCorppassPersonnel = corppassPersonnelMap.get(uuid);
                  const existingPortalUsPerson = this.portalUsPersonOptionsMap$.getValue().get(uuid);

                  if (existingPortalPersonnelSelected?.usPerson) {
                    // console.log('formControlChanges$', {
                    //   currentForm: form.getRawValue(),
                    //   usPersonExistingInPortalPersonnel: existingPortalPersonnelSelected,
                    // });

                    const self = form.getRawValue()?.[UsPersonModelKeys.self];
                    if (self) {
                      form.patchValue({ ...self, self: null });
                      return;
                    }

                    form.patchValue(existingPortalPersonnelSelected.usPerson);
                  } else if (existingCorppassPersonnel?.usPerson) {
                    // console.log('formControlChanges$', {
                    //   currentForm: form.getRawValue(),
                    //   usPersonExistingInCorpPassPersonnel: existingCorppassPersonnel,
                    // });

                    const self = form.getRawValue()?.[UsPersonModelKeys.self];
                    if (self) {
                      form.patchValue({ ...self, self: null });
                      return;
                    }

                    form.patchValue(existingCorppassPersonnel.usPerson);
                  } else if (existingPortalUsPerson?.usPerson) {
                    const self = form.getRawValue()?.[UsPersonModelKeys.self];
                    if (self) {
                      form.patchValue({ ...self, self: null });
                      return;
                    }

                    form.patchValue(existingPortalUsPerson.usPerson);
                  }
                }),
              ),
              this.portalPersonnelOptionsMap$.pipe(
                debounceTime(300),
                tap(() => mergePortalPersonnelOptionsMapToPortalUsPersonOptionsMap()),
                tap(() => {
                  const uuid = getUuidSelected();
                  if (!uuid || !(form instanceof FormGroup)) return;

                  const portalOptions = getPortalOptions();

                  const portalPersonnelShareholderSelectedMap = transformToUsPersonOptionMap(portalOptions.selected.options, us => {
                    us.method = Method.populate;
                  }).map;

                  const corppassPersonnelMap = transformToUsPersonOptionMap(portalOptions.corppass.options, us => {
                    us.method = Method.corppass;
                  }).map;

                  const existingPortalOption = portalPersonnelShareholderSelectedMap.get(uuid);
                  const existingCorpPassOption = corppassPersonnelMap.get(uuid);

                  if (existingPortalOption?.usPerson) {
                    // console.log({
                    //   currentForm: form.getRawValue(),
                    //   usPersonExistingInPortalPersonnel: existingPortalOption,
                    // });

                    // set usCitizenTaxNumber to the form
                    // get US Person from options
                    const portalUsPersonOptions = this.portalUsPersonOptions$.getValue();
                    const selectedOption = portalUsPersonOptions.find(v => v.value === uuid);
                    const currentTaxNumber = form.getRawValue()?.[UsPersonModelKeys.usCitizenTaxNumber];

                    if (selectedOption?.usPerson) {
                      const { usCitizenTaxNumber } = selectedOption.usPerson;
                      existingPortalOption.usPerson[UsPersonModelKeys.usCitizenTaxNumber] = usCitizenTaxNumber || currentTaxNumber || null;
                    } else if (currentTaxNumber) {
                      existingPortalOption.usPerson[UsPersonModelKeys.usCitizenTaxNumber] = currentTaxNumber;
                    }

                    form.patchValue(existingPortalOption.usPerson);
                  } else {
                    // if (existingCorpPassOption?.usPerson) {
                    //   console.log({
                    //     currentForm: form.getRawValue(),
                    //     usPersonExistingInCorpPassPersonnel: existingCorpPassOption,
                    //   });
                    //   form.patchValue({ [UsPersonModelKeys.method]: Method.corppass });
                    // }

                    const method = form?.getRawValue()?.[UsPersonModelKeys.method];
                    if (method === Method.populate) {
                      const parentProps = parent?.parent?.props;
                      const usPersons: UsPersonModel[] = parent?.parent?.model ?? [];

                      if (!existingPortalOption && model?.[UsPersonModelKeys.uuid] !== PERSONNEL_OTHER) {
                        const indexToRemove = usPersons.findIndex(v => v[UsPersonModelKeys.uuid] === formControl?.value);
                        if (indexToRemove !== -1) {
                          parentProps?.['remove']?.(indexToRemove);
                        }
                      }
                    }
                  }

                  // const portalPersonnelShareholderSelectedMap = transformToUsPersonOptionMap(portalOptions.selected, us => {
                  //   us.method = Method.populate;
                  // }).map;
                  //
                  // populateFormBasedOnUsPersonOptionMap(portalPersonnelShareholderSelectedMap);
                  // populateMethodBasedOnRemainingPersonnelOptionMap(portalOptions.remaining.map); // make sure call after populateFormBasedOnUsPersonOptionMap
                }),
                tap(() => removeFormControl()),
              ),
            ]);
          },
        },
        props: {
          label: 'List of Shareholder',
          placeholder: 'Please select',
          options: this.portalUsPersonOptions$,
          required: true,
        },
      },
      {
        key: UsPersonModelKeys.name,
        type: 'input',
        expressions: { hide: field => this.isPopulateMethod(field) },
        resetOnHide: false,
        props: {
          label: 'Name as per NRIC/Passport',
          placeholder: 'Please enter input',
          required: true,
          maxLength: COMMON_VALIDATION.MAX_LENGTH,
          maskConfig: {
            mask: 'a*',
            config: { patterns: { a: { pattern: INDIVIDUAL_NAME.PATTERN } }, dropSpecialCharacters: false },
          },
        },
      },
      {
        key: UsPersonModelKeys.nricNumber,
        type: 'input',
        expressions: { 'props.disabled': field => this.isPopulateMethod(field) },
        props: {
          label: 'NRIC/Passport No.',
          placeholder: 'e.g. S1234567D',
          minLength: NRIC.MIN_LENGTH,
          maxLength: NRIC.MAX_LENGTH,
          required: true,
          maskConfig: { mask: 'A*' },
        },
        // validators: { validation: ['nric'] },
        parsers: [uppercaseParser],
      },
      {
        key: UsPersonModelKeys.dateOfBirthOnly,
        type: 'datepicker',
        expressions: { hide: field => this.isPopulateMethod(field) },
        resetOnHide: false,
        validators: { validation: ['dateRange'] },
        props: {
          label: 'Date of birth',
          placeholder: 'Please enter input',
          // minDate: getDate100YearsAgo(),
          // maxDate: getEndOfToday(),
          maxDate: get18YearsAgo(),
        },
      },
      {
        expressions: { hide: field => this.isPopulateMethod(field) },
        resetOnHide: false,
        fieldGroup: this.mobileFormConfigService.initMobileFieldConfig({
          label: 'Mobile Number',
          key: { areaCode: UsPersonModelKeys.contactAreaCode, number: UsPersonModelKeys.contactNumber },
        }),
      },
      {
        key: UsPersonModelKeys.email,
        type: 'input',
        resetOnHide: false,
        expressions: { hide: field => this.isPopulateMethod(field) },
        props: {
          label: 'Email Address',
          placeholder: 'Please enter input',
          required: true,
          maxLength: EMAIL.MAX_LENGTH,
        },
        validators: {
          validation: [{ name: 'email-custom', options: { keyName: UsPersonModelKeys.email } }],
        },
        // parsers: [lowercaseParser],
      },
      {
        key: UsPersonModelKeys.appointment,
        resetOnHide: false,
        className: 'col-start-1',
        expressions: { 'props.disabled': field => this.isPopulateMethod(field) },
        validators: {
          validation: [{ name: 'must-have-shareholder', options: { keyName: UsPersonModelKeys.appointment } }],
        },
        type: '#appointment',
        props: { required: true, multiple: true },
        hooks: { onInit: field => constraintAppointmentOptions({ field }) },
      },
      {
        key: UsPersonModelKeys.share,
        type: 'number',
        expressions: { 'props.disabled': field => this.isPopulateMethod(field) },
        props: {
          label: 'Shareholding Percentage',
          placeholder: 'e.g. 30',
          // maskConfig: {
          //   mask: 'separator.2',
          // },
          required: true,
          suffix: '%',
        },
        validators: {
          validation: ['percentage'],
        },
      },
      {
        key: UsPersonModelKeys.usCitizenTaxNumber,
        validators: { validation: ['tin'] },
        parsers: [uppercaseParser],
        type: 'input',
        props: {
          label: 'Taxpayer Identification No.',
          placeholder: 'Please enter input',
        },
        // expressions: { 'props.disabled': field => this.isPopulateMethod(field) },
      },
    ];
  }

  private isPopulateMethod({ model }: PortalFieldConfig): boolean {
    return typeof model?.[UsPersonModelKeys.uuid] === 'string' && model?.[UsPersonModelKeys.method] === Method.populate;
  }

  private isNotOtherOption({ model }: PortalFieldConfig): boolean {
    return typeof model?.[UsPersonModelKeys.uuid] === 'string' && model?.[UsPersonModelKeys.uuid] !== PERSONNEL_OTHER;
  }
}
